// ============================================
// JA MOTORIST RIGHTS - ENHANCED OFFENCES PAGE
// Category-first, Accordion expansion, Smart search
// ============================================

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Search, X, FileText, Shield, Gauge, AlertTriangle, Users,
  Lock, AlertOctagon, Wine, Settings, Clipboard, Truck,
  AlertCircle, Bike, ParkingCircle, MoreHorizontal,
  ChevronDown, ChevronUp, ChevronLeft, Info, Scale, Clock, DollarSign, Award,
  Star, Copy, Share2, TrendingUp, History, Check,
  Bookmark, BookmarkPlus, ExternalLink, Lightbulb, ArrowUp,
  Volume2, VolumeX, LogIn
} from 'lucide-react';

// Import enhanced offences data
import offencesData from '../data/ja-offences-enhanced.json';
import { useAuth } from '../contexts/AuthContext';
import { savedOffences as savedOffencesDb } from '../lib/supabase';
import { TopBanner, BottomBanner, InlineBanner1, InlineBanner2 } from './BannerAd';

// ============================================
// CONSTANTS & HELPERS
// ============================================

const CATEGORY_ICONS = {
  licensing: FileText,
  insurance: Shield,
  speeding: Gauge,
  traffic_signals: AlertTriangle,
  pedestrian: Users,
  seatbelt: Lock,
  dangerous_driving: AlertOctagon,
  dui: Wine,
  equipment: Settings,
  documentation: Clipboard,
  commercial: Truck,
  hazmat: AlertCircle,
  motorcycle: Bike,
  parking: ParkingCircle,
  misc: MoreHorizontal
};

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: '#22C55E', bg: '#DCFCE7' },
  medium: { label: 'Medium', color: '#F59E0B', bg: '#FEF3C7' },
  high: { label: 'High', color: '#EF4444', bg: '#FEE2E2' },
  critical: { label: 'Critical', color: '#7C2D12', bg: '#FECACA' }
};


// LocalStorage keys
const STORAGE_KEYS = {
  viewCounts: 'ja_offences_views',
  bookmarks: 'ja_offences_bookmarks',
  recentSearches: 'ja_offences_recent_searches'
};

// Mapping from chatbot offence keys to enhanced offence codes
// This bridges the gap between the simple keys used in the chatbot
// and the structured codes in the enhanced offences database
const CHATBOT_KEY_TO_CODE = {
  // Licensing
  'no_licence': 'D001',
  'no_license': 'D001',
  'expired_licence': 'D002',
  'no_insurance': 'D013',
  'no_registration': 'D007',
  'no_number_plates': 'D012',
  'obscured_plates': 'D012',

  // DUI
  'dui': 'D044',
  'drunk_driving': 'D044',
  'refusing_breath_test': 'D047',

  // Speeding
  'speeding': 'D015',
  'school_zone_speeding': 'D019',

  // Safety Equipment
  'no_seatbelt': 'D033',
  'seatbelt': 'D033',
  'no_helmet': 'D085',
  'helmet': 'D085',

  // Parking
  'illegal_parking': 'D094',
  'parking': 'D094',
  'double_parking': 'D094',

  // Vehicle Equipment
  'tinted_windows': 'D056',
  'tint': 'D056',
  'no_lights': 'D133',
  'no_headlights': 'D133',
  'improper_lights': 'D051',
  'defective_lights': 'D050',
  'no_indicators': 'D066',
  'turn_signals': 'D066',

  // Dangerous Driving
  'careless_driving': 'D038',
  'dangerous_driving': 'D037',
  'reckless_driving': 'D037',
  'racing': 'D041',

  // Phone/Distraction
  'phone_while_driving': 'D096',
  'mobile_phone': 'D096',
  'phone': 'D096',
  'texting': 'D096',

  // Fitness
  'no_fitness': 'D068',
  'expired_fitness': 'D068',
  'fitness': 'D068',

  // Traffic Signals
  'running_red_light': 'D021',
  'red_light': 'D021',
  'traffic_light': 'D021',

  // Accidents
  'hit_and_run': 'D042',
  'leaving_scene': 'D042',
  'fail_to_report_accident': 'D043',

  // Child Safety
  'child_restraint': 'D035',
  'child_restraint_violation': 'D035',
  'child_seat': 'D035',

  // Following Distance
  'following_too_close': 'D147',
  'tailgating': 'D147',

  // Vehicle Defects
  'defective_brakes': 'D049',
  'brakes': 'D049',
  'defective_tyres': 'D055',
  'defective_tires': 'D055',
  'bald_tyres': 'D055',
  'bald_tires': 'D055',

  // Learner Violations
  'learner_unaccompanied': 'D004',
  'learner_night_driving': 'D005',
  'learner_violation': 'D004',

  // Bus Lane
  'bus_lane': 'D026',
  'bus_lane_violation': 'D026',

  // Licensing - Additional
  'driving_wrong_class': 'D003',
  'wrong_class': 'D003',
  'driving_while_disqualified': 'D122',
  'disqualified': 'D122',
  'learner_without_l_plates': 'D006',
  'no_l_plates': 'D006',
  'l_plates': 'D006',
  'learner_on_highway': 'D005',

  // Speed - Additional
  'minimum_speed_violation': 'D020',
  'too_slow': 'D020',

  // Motorcycle - Additional
  'helmet_violation': 'D085',
  'motorcycle_pillion_violation': 'D086',
  'pillion_no_helmet': 'D086',
  'motorcycle_protective_gear': 'D087',
  'motorcycle_lane_splitting': 'D025',

  // Lane Changes & Overtaking
  'improper_lane_change': 'D025',
  'lane_change': 'D025',
  'improper_overtaking': 'D028',
  'overtaking': 'D028',

  // Parking - Additional
  'parking_violations': 'D094',
  'bus_stop_parking': 'D090',

  // Pedestrian
  'pedestrian_crossing_violation': 'D031',
  'fail_yield_pedestrian': 'D031',

  // Traffic Signals - Additional
  'failure_to_stop': 'D022',
  'stop_sign': 'D022',
  'failure_to_yield': 'D125',
  'roundabout': 'D124',
  'u_turn_violation': 'D027',
  'u_turn': 'D027',

  // Emergency Vehicles
  'emergency_vehicle_obstruction': 'D098',
  'blocking_emergency': 'D098',
  'following_emergency_vehicle': 'D099',

  // Noise & Horn
  'excessive_noise': 'D057',
  'loud_exhaust': 'D057',
  'no_horn': 'D131',
  'improper_horn_use': 'D132',
  'horn_misuse': 'D132',

  // Vehicle Equipment - Additional
  'no_mirrors': 'D061',
  'defective_mirrors': 'D061',
  'no_windscreen_wipers': 'D062',
  'defective_wipers': 'D062',
  'defective_steering': 'D060',
  'no_sidelights': 'D133',

  // Commercial/PPV
  'no_ppv_licence': 'D071',
  'ppv_violation': 'D071',
  'taxi_overcharging': 'D139',
  'refusing_fare': 'D139',
  'pirating': 'D071',
  'overloading_passengers': 'D138',
  'overloading_goods': 'D140',
  'overloaded': 'D138',

  // DUI - Additional
  'drug_driving': 'D044',
  'driving_unfit_vehicle': 'D068',

  // Additional Aliases
  'racing_on_road': 'D041',
  'failing_to_report_accident': 'D043',

  // Wrong Way / Reversing
  'wrong_way_driving': 'D126',
  'wrong_way': 'D126',
  'reversing_unsafely': 'D097',
  'excessive_reversing': 'D097',

  // Blue Lights / Emergency Impersonation
  'unauthorized_blue_lights': 'D052',
  'blue_lights': 'D052',
  'impersonating_emergency': 'D052',

  // Load Violations
  'insecure_load': 'D107',
  'projecting_load': 'D107',
  'load_projection': 'D107',

  // Hazmat / Dangerous Goods
  'dangerous_goods_violation': 'D080',
  'hazmat': 'D080',
  'hazmat_transport': 'D080',

  // Child Safety - Additional (smoking with child maps to child restraint as closest)
  'smoking_in_vehicle_with_child': 'D035',

  // Obstruction
  'obstructing_traffic': 'D143',
  'blocking_traffic': 'D143',

  // Unauthorized Vehicle Use (maps to fail to secure as closest)
  'using_vehicle_unauthorized': 'D146'
};

// ============================================
// CUSTOM HOOKS
// ============================================

const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
};

// ============================================
// HIGHLIGHT TEXT COMPONENT
// ============================================

const HighlightText = ({ text, highlight }) => {
  if (!highlight || !text) return <span>{text}</span>;

  const parts = text.split(new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

// ============================================
// SEVERITY BADGE COMPONENT
// ============================================

const SeverityBadge = ({ severity }) => {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.low;
  return (
    <span
      className="text-xs font-bold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: config.bg, color: config.color }}
    >
      {config.label}
    </span>
  );
};

// ============================================
// PENALTY COMPARISON COMPONENT
// ============================================

const PenaltyComparison = ({ offence, allOffences }) => {
  const stats = useMemo(() => {
    const fines = allOffences.map(o => o.maxFine).sort((a, b) => a - b);
    const currentFine = offence.maxFine;
    const percentile = (fines.filter(f => f <= currentFine).length / fines.length) * 100;
    const maxFine = Math.max(...fines);
    const widthPercent = (currentFine / maxFine) * 100;

    return { percentile: Math.round(percentile), widthPercent, maxFine };
  }, [offence.maxFine, allOffences]);

  return (
    <div className="bg-slate-50 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 text-slate-600 mb-3">
        <TrendingUp className="w-4 h-4" />
        <span className="text-sm font-medium">How This Compares</span>
      </div>
      <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="absolute h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full transition-all duration-500"
          style={{ width: `${stats.widthPercent}%` }}
        />
        <div
          className="absolute h-full w-1 bg-slate-800 rounded"
          style={{ left: `${stats.widthPercent}%`, transform: 'translateX(-50%)' }}
        />
      </div>
      <div className="flex justify-between mt-2 text-xs text-slate-500">
        <span>$2,000</span>
        <span className="font-medium text-slate-700">
          ${offence.maxFine.toLocaleString()} ({stats.percentile}th percentile)
        </span>
        <span>${stats.maxFine.toLocaleString()}</span>
      </div>
      <p className="text-xs text-slate-600 mt-2">
        This fine is higher than {stats.percentile}% of all traffic offences
      </p>
    </div>
  );
};

// ============================================
// EXPANDED OFFENCE DETAILS COMPONENT
// ============================================

const ExpandedOffenceDetails = ({
  offence,
  category,
  allOffences,
  onViewRelated,
  isBookmarked,
  onToggleBookmark,
  searchQuery
}) => {
  const [copied, setCopied] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  // Web Audio Reader
  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utterance);
      setSpeaking(true);
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, []);

  const handleListen = () => {
    if (speaking) {
      stopSpeaking();
    } else {
      const text = `${offence.shortTitle}. ${offence.description}.
        The maximum fine is ${offence.maxFine.toLocaleString()} dollars.
        ${offence.fixedPenalty ? `Fixed penalty is ${offence.fixedPenalty.toLocaleString()} dollars.` : 'This offence requires a court appearance.'}
        ${offence.demeritPoints ? `${offence.demeritPoints} demerit points.` : ''}
        ${offence.imprisonment ? `Maximum imprisonment is ${offence.imprisonment}.` : ''}
        ${offence.tip ? `Tip: ${offence.tip}` : ''}`;
      speak(text);
    }
  };

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  const handleCopy = async () => {
    const text = `${offence.shortTitle} (${offence.code})
Regulation: ${offence.regulation}
Max Fine: $${offence.maxFine.toLocaleString()}
${offence.fixedPenalty ? `Fixed Penalty: $${offence.fixedPenalty.toLocaleString()}` : 'No fixed penalty - court required'}
${offence.imprisonment ? `Imprisonment: Up to ${offence.imprisonment}` : ''}
${offence.demeritPoints ? `Demerit Points: ${offence.demeritPoints}` : ''}

${offence.description}

Source: Jamaica Road Traffic Act, 2018 & Regulations, 2022`;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: offence.shortTitle,
          text: `${offence.shortTitle}: Max fine $${offence.maxFine.toLocaleString()}. ${offence.description.slice(0, 100)}...`,
          url: window.location.href
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      handleCopy();
    }
  };

  const relatedOffences = useMemo(() => {
    return (offence.relatedOffences || [])
      .map(code => allOffences.find(o => o.code === code))
      .filter(Boolean);
  }, [offence.relatedOffences, allOffences]);

  return (
    <div className="bg-gray-50 border-t border-gray-200 p-4 space-y-4 animate-in slide-in-from-top duration-200">
      {/* Action Buttons */}
      <div className="flex gap-1.5 justify-end">
        <button
          onClick={handleListen}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            speaking
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          {speaking ? <VolumeX className="w-3.5 h-3.5 animate-pulse" /> : <Volume2 className="w-3.5 h-3.5" />}
          {speaking ? 'Stop' : 'Read'}
        </button>
        <button
          onClick={onToggleBookmark}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isBookmarked
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          {isBookmarked ? <Bookmark className="w-3.5 h-3.5 fill-current" /> : <Bookmark className="w-3.5 h-3.5" />}
          Save
        </button>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-xs font-medium text-gray-600 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          Copy
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-xs font-medium text-gray-600 transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share
        </button>
      </div>

      {/* Description */}
      <div>
        <p className="text-gray-700 leading-relaxed">
          <HighlightText text={offence.description} highlight={searchQuery} />
        </p>
      </div>

      {/* Tip Box */}
      {offence.tip && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">{offence.tip}</p>
        </div>
      )}

      {/* Penalties Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center gap-2 text-green-700 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">Fixed Penalty</span>
          </div>
          <p className="text-lg font-bold text-green-800">
            {offence.fixedPenalty ? `$${offence.fixedPenalty.toLocaleString()}` : 'N/A'}
          </p>
        </div>

        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center gap-2 text-red-700 mb-1">
            <Scale className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">Max Court Fine</span>
          </div>
          <p className="text-lg font-bold text-red-800">
            ${offence.maxFine.toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <Award className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">Demerit Points</span>
          </div>
          <p className="text-lg font-bold text-amber-800">
            {offence.demeritPoints || '0'}
          </p>
        </div>

        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center gap-2 text-purple-700 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">Max Imprisonment</span>
          </div>
          <p className="text-lg font-bold text-purple-800">
            {offence.imprisonment || 'None'}
          </p>
        </div>
      </div>

      {/* Court Required Badge */}
      {offence.courtRequired && (
        <div className="bg-red-100 border border-red-200 rounded-xl p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-700 font-medium">Court appearance required - no fixed penalty option</span>
        </div>
      )}

      {/* Penalty Comparison */}
      <PenaltyComparison offence={offence} allOffences={allOffences} />

      {/* Legal Basis */}
      {offence.legalBasis && (
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Scale className="w-4 h-4 text-gray-500" />
            Legal Basis
          </h4>
          <p className="text-gray-600 text-sm leading-relaxed">{offence.legalBasis}</p>
        </div>
      )}

      {/* What Constitutes This Offence */}
      {offence.whatConstitutes?.length > 0 && (
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
          <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-orange-500" />
            What Constitutes This Offence
          </h4>
          <ul className="space-y-1.5">
            {offence.whatConstitutes.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-orange-700 text-sm">
                <span className="text-orange-400 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Common Scenarios */}
      {offence.commonScenarios?.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-500" />
            Common Scenarios
          </h4>
          <ul className="space-y-1.5">
            {offence.commonScenarios.map((scenario, idx) => (
              <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                <span className="text-blue-400 mt-1">•</span>
                <span>{scenario}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Your Rights */}
      {offence.rights?.length > 0 && (
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
          <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" />
            Your Rights
          </h4>
          <ul className="space-y-1.5">
            {offence.rights.map((right, idx) => (
              <li key={idx} className="flex items-start gap-2 text-emerald-700 text-sm">
                <span className="text-emerald-500 mt-1">✓</span>
                <span>{right}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Possible Defenses */}
      {offence.defenses?.length > 0 && (
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
          <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
            <Scale className="w-4 h-4 text-indigo-500" />
            Possible Defenses
          </h4>
          <ul className="space-y-1.5">
            {offence.defenses.map((defense, idx) => (
              <li key={idx} className="flex items-start gap-2 text-indigo-700 text-sm">
                <span className="text-indigo-400 mt-1">•</span>
                <span>{defense}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Related Offences */}
      {relatedOffences.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-gray-500" />
            Related Offences
          </h4>
          <div className="flex flex-wrap gap-2">
            {relatedOffences.map(related => (
              <button
                key={related.code}
                onClick={() => onViewRelated(related)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
              >
                <span className="font-mono text-xs text-gray-500">{related.code}</span>
                <span className="text-gray-700">{related.shortTitle}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// OFFENCE CARD COMPONENT (Accordion Item)
// ============================================

const OffenceCard = ({
  offence,
  category,
  isExpanded,
  onToggle,
  allOffences,
  onViewRelated,
  isBookmarked,
  onToggleBookmark,
  searchQuery
}) => {
  const Icon = CATEGORY_ICONS[offence.category] || FileText;
  const cat = category || {};

  return (
    <div
      id={`offence-${offence.code}`}
      className={`bg-white rounded-2xl transition-all duration-200 shadow-sm ${
        isExpanded ? 'shadow-xl ring-2 ring-blue-500 ring-opacity-50' : 'hover:shadow-md border border-gray-100'
      }`}
    >
      {/* Card Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left flex items-center gap-4"
      >
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ backgroundColor: cat.color || '#6B7280' }}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-bold text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">{offence.code}</span>
            <SeverityBadge severity={offence.severity} />
            {offence.courtRequired && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">Court</span>
            )}
            {isBookmarked && (
              <Bookmark className="w-4 h-4 text-amber-500 fill-amber-500" />
            )}
          </div>
          <h3 className="font-bold text-gray-800">
            <HighlightText text={offence.shortTitle} highlight={searchQuery} />
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {offence.fixedPenalty
              ? <span className="text-green-600 font-semibold">Fixed: ${offence.fixedPenalty.toLocaleString()}</span>
              : <span className="text-red-600 font-semibold">Max: ${offence.maxFine.toLocaleString()}</span>
            }
            {offence.demeritPoints > 0 && <span className="text-amber-600"> • {offence.demeritPoints} pts</span>}
            <span className="text-gray-400"> • {offence.regulation}</span>
          </p>
        </div>

        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          isExpanded ? 'bg-blue-100' : 'bg-gray-100'
        }`}>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-blue-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <ExpandedOffenceDetails
          offence={offence}
          category={cat}
          allOffences={allOffences}
          onViewRelated={onViewRelated}
          isBookmarked={isBookmarked}
          onToggleBookmark={onToggleBookmark}
          searchQuery={searchQuery}
        />
      )}
    </div>
  );
};

// ============================================
// CATEGORY CARD COMPONENT
// ============================================

const CategoryCard = ({ category, offenceCount, isSelected, onClick }) => {
  const Icon = CATEGORY_ICONS[category.id] || FileText;

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-2xl transition-all flex items-center gap-3 text-left shadow-sm hover:shadow-lg ${
        isSelected
          ? 'ring-2 ring-offset-2 shadow-lg scale-[1.02]'
          : 'bg-white hover:scale-[1.01]'
      }`}
      style={{
        backgroundColor: isSelected ? category.color : 'white',
        ringColor: category.color
      }}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isSelected ? 'bg-white/20' : ''
        }`}
        style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : category.color + '15' }}
      >
        <Icon
          className="w-5 h-5"
          style={{ color: isSelected ? 'white' : category.color }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className={`font-bold text-base leading-tight ${isSelected ? 'text-white' : 'text-gray-800'}`}>
          {category.name}
        </h3>
        <p className={`text-sm mt-0.5 ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
          {offenceCount} offences
        </p>
      </div>
    </button>
  );
};

// ============================================
// BOOKMARKED SECTION COMPONENT
// ============================================

const BookmarkedSection = ({ offences, bookmarks, onSelect, onClearAll }) => {
  const bookmarkedOffences = useMemo(() => {
    return offences.filter(o => bookmarks.includes(o.code));
  }, [offences, bookmarks]);

  if (bookmarkedOffences.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center">
            <Star className="w-4 h-4 text-white" />
          </div>
          <h2 className="font-bold text-gray-800">Saved Offences</h2>
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-semibold">
            {bookmarkedOffences.length}
          </span>
        </div>
        <button
          onClick={onClearAll}
          className="text-xs text-gray-500 hover:text-red-600 font-medium"
        >
          Clear all
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {bookmarkedOffences.slice(0, 4).map(offence => (
          <button
            key={offence.code}
            onClick={() => onSelect(offence)}
            className="bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl p-4 text-left hover:shadow-lg transition-all hover:scale-[1.02] text-white"
          >
            <div className="flex items-center gap-2 mb-2">
              <Bookmark className="w-4 h-4 text-white fill-white" />
              <span className="text-xs font-mono bg-white/20 px-2 py-0.5 rounded">{offence.code}</span>
            </div>
            <h3 className="font-semibold text-sm leading-tight line-clamp-2">
              {offence.shortTitle}
            </h3>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// SEARCH COMPONENT WITH AUTOCOMPLETE
// ============================================

const SearchBar = ({
  value,
  onChange,
  onClear,
  recentSearches,
  onSelectRecent,
  onClearRecent,
  suggestions,
  onSelectSuggestion
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const showDropdown = isFocused && (
    (value.length === 0 && recentSearches.length > 0) ||
    (value.length > 0 && suggestions.length > 0)
  );

  return (
    <div className="relative">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        placeholder="Search offences, codes, or regulations..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        className="w-full pl-12 pr-10 py-3 rounded-xl bg-white border-2 border-gray-200 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
      />
      {value && (
        <button
          onClick={onClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
          {/* Recent Searches */}
          {value.length === 0 && recentSearches.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <History className="w-4 h-4" />
                  <span>Recent Searches</span>
                </div>
                <button
                  onClick={onClearRecent}
                  className="text-xs text-gray-400 hover:text-red-500"
                >
                  Clear
                </button>
              </div>
              {recentSearches.slice(0, 5).map((term, idx) => (
                <button
                  key={idx}
                  onClick={() => onSelectRecent(term)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm flex items-center gap-2"
                >
                  <History className="w-4 h-4 text-gray-400" />
                  {term}
                </button>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {value.length > 0 && suggestions.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-sm text-gray-600">Suggestions</span>
              </div>
              {suggestions.map(offence => (
                <button
                  key={offence.code}
                  onClick={() => onSelectSuggestion(offence)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                >
                  <span className="text-xs font-mono text-gray-400">{offence.code}</span>
                  <span className="text-gray-700 text-sm">{offence.shortTitle}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN OFFENCES PAGE COMPONENT
// ============================================

const OffencesPage = ({ setPage, goBack, lang = 'en', selectedOffenceCode, clearSelectedOffence }) => {
  const { categories, offences } = offencesData;
  const { user, isAuthenticated } = useAuth();

  // Refs
  const offencesListRef = useRef(null);
  const dbSyncedRef = useRef(false);

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedOffence, setExpandedOffence] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // LocalStorage state
  const [viewCounts, setViewCounts] = useLocalStorage(STORAGE_KEYS.viewCounts, {});
  const [bookmarks, setBookmarks] = useLocalStorage(STORAGE_KEYS.bookmarks, []);
  const [recentSearches, setRecentSearches] = useLocalStorage(STORAGE_KEYS.recentSearches, []);

  // Sync bookmarks with database for logged-in users
  useEffect(() => {
    const syncBookmarks = async () => {
      if (isAuthenticated && user && !dbSyncedRef.current) {
        dbSyncedRef.current = true;
        try {
          const { data } = await savedOffencesDb.getAll(user.id);
          if (data && data.length > 0) {
            // DB has bookmarks, use them
            const dbCodes = data.map(d => d.offence_code);
            setBookmarks(dbCodes);
          } else if (bookmarks.length > 0) {
            // No DB bookmarks but local ones exist, sync to DB
            await savedOffencesDb.sync(user.id, bookmarks);
          }
        } catch (error) {
          console.error('Error syncing bookmarks:', error);
        }
      }
    };
    syncBookmarks();
  }, [isAuthenticated, user]);

  // Check for offence to open from dashboard navigation
  useEffect(() => {
    const codeToOpen = localStorage.getItem('ja_offence_to_open');
    if (codeToOpen && offences.length > 0) {
      localStorage.removeItem('ja_offence_to_open');
      const offence = offences.find(o => o.code === codeToOpen);
      if (offence) {
        setSelectedCategory(offence.category);
        setTimeout(() => {
          setExpandedOffence(offence);
          if (offencesListRef.current) {
            const yOffset = -80;
            const y = offencesListRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 150);
      }
    }
  }, [offences]);

  // Handle deep-link from chatbot - open specific offence
  useEffect(() => {
    let timeoutId = null;

    if (selectedOffenceCode?.code) {
      // First, try to map chatbot keys to enhanced codes
      const inputCode = selectedOffenceCode.code;
      const mappedCode = CHATBOT_KEY_TO_CODE[inputCode.toLowerCase()] || inputCode.toUpperCase();

      // Find the offence by code (try mapped code first, then original)
      let offence = offences.find(o => o.code === mappedCode || o.code.toUpperCase() === mappedCode);

      // If not found by code, try to find by keyword/title matching
      if (!offence && inputCode) {
        const searchTerm = inputCode.toLowerCase().replace(/_/g, ' ');
        offence = offences.find(o =>
          o.shortTitle.toLowerCase().includes(searchTerm) ||
          o.title.toLowerCase().includes(searchTerm) ||
          (o.keywords || []).some(k => k.toLowerCase().includes(searchTerm))
        );
      }

      if (offence) {
        // Set the category
        setSelectedCategory(offence.category);
        // Expand the offence after a short delay to let the list render
        timeoutId = setTimeout(() => {
          setExpandedOffence(offence);
          // Clear the selection after expanding so it doesn't re-trigger
          if (clearSelectedOffence) clearSelectedOffence();
          // Scroll to the offences list
          if (offencesListRef.current) {
            const yOffset = -80;
            const y = offencesListRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 150);
      } else if (selectedOffenceCode.category) {
        // If offence not found but category provided, at least select the category
        setSelectedCategory(selectedOffenceCode.category);
        // Clear the selection so it doesn't re-trigger
        if (clearSelectedOffence) clearSelectedOffence();
      } else {
        // No match found, clear the selection
        if (clearSelectedOffence) clearSelectedOffence();
      }
    }

    // Cleanup: cancel timeout if component unmounts or effect re-runs
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedOffenceCode, offences, clearSelectedOffence]);

  // Back to top scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToOffences = () => {
    setTimeout(() => {
      if (offencesListRef.current) {
        const yOffset = -100; // Offset to show more content above
        const y = offencesListRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  // Category offence counts
  const categoryOffenceCounts = useMemo(() => {
    const counts = {};
    categories.forEach(cat => {
      counts[cat.id] = offences.filter(o => o.category === cat.id).length;
    });
    return counts;
  }, [categories, offences]);

  // Search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase();
    return offences
      .filter(o =>
        o.shortTitle.toLowerCase().includes(query) ||
        o.code.toLowerCase().includes(query) ||
        o.title.toLowerCase().includes(query)
      )
      .slice(0, 5);
  }, [searchQuery, offences]);

  // Filter offences
  const filteredOffences = useMemo(() => {
    let filtered = offences;

    // If searching, show results across all categories
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = offences.filter(o =>
        o.title.toLowerCase().includes(query) ||
        o.shortTitle.toLowerCase().includes(query) ||
        o.code.toLowerCase().includes(query) ||
        o.description.toLowerCase().includes(query) ||
        o.regulation.toLowerCase().includes(query) ||
        (o.keywords || []).some(k => k.includes(query))
      );
    } else if (selectedCategory) {
      // Only show category offences if not searching
      filtered = offences.filter(o => o.category === selectedCategory);
    } else {
      // No search and no category = empty (category-first approach)
      filtered = [];
    }

    return filtered;
  }, [offences, selectedCategory, searchQuery]);

  // Get category for offence
  const getCategoryForOffence = useCallback((offence) => {
    return categories.find(c => c.id === offence.category);
  }, [categories]);

  // Handlers
  const handleToggleExpand = useCallback((offence) => {
    if (expandedOffence?.code === offence.code) {
      setExpandedOffence(null);
    } else {
      setExpandedOffence(offence);
      // Increment view count
      setViewCounts(prev => ({
        ...prev,
        [offence.code]: (prev[offence.code] || 0) + 1
      }));
    }
  }, [expandedOffence, setViewCounts]);

  const handleToggleBookmark = useCallback(async (code) => {
    // Require login to save offences
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    const isCurrentlyBookmarked = bookmarks.includes(code);

    // Update local state immediately
    setBookmarks(prev =>
      isCurrentlyBookmarked
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );

    // Sync with database
    try {
      if (isCurrentlyBookmarked) {
        await savedOffencesDb.remove(user.id, code);
      } else {
        await savedOffencesDb.add(user.id, code);
      }
    } catch (error) {
      console.error('Error syncing bookmark:', error);
    }
  }, [bookmarks, setBookmarks, isAuthenticated, user]);

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    // Clear category when searching
    if (value.trim()) {
      setSelectedCategory(null);
    }
  }, []);

  const handleSearchSubmit = useCallback(() => {
    if (searchQuery.trim() && !recentSearches.includes(searchQuery.trim())) {
      setRecentSearches(prev => [searchQuery.trim(), ...prev].slice(0, 10));
    }
  }, [searchQuery, recentSearches, setRecentSearches]);

  const handleSelectCategory = useCallback((categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryId);
      setSearchQuery(''); // Clear search when selecting category
      setExpandedOffence(null);
      scrollToOffences(); // Auto scroll to offences list
    }
  }, [selectedCategory]);

  const handleViewRelated = useCallback((offence) => {
    setExpandedOffence(offence);
    // Find and select the category
    setSelectedCategory(offence.category);
    setSearchQuery('');
    // Scroll to the specific offence card after a short delay to allow rendering
    setTimeout(() => {
      const element = document.getElementById(`offence-${offence.code}`);
      if (element) {
        const yOffset = -120; // Buffer to show content above the card
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 150);
  }, []);

  const handleSelectSuggestion = useCallback((offence) => {
    setSearchQuery(offence.shortTitle);
    setExpandedOffence(offence);
    handleSearchSubmit();
    scrollToOffences(); // Auto scroll to the result
  }, [handleSearchSubmit]);

  const handleToggleFilter = useCallback((filterId) => {
    setActiveFilters(prev =>
      prev.includes(filterId)
        ? prev.filter(f => f !== filterId)
        : [...prev, filterId]
    );
  }, []);

  const handleClearBookmarks = useCallback(async () => {
    if (window.confirm('Clear all saved offences?')) {
      setBookmarks([]);

      // Clear from database if logged in
      if (isAuthenticated && user) {
        try {
          await savedOffencesDb.clear(user.id);
        } catch (error) {
          console.error('Error clearing bookmarks from database:', error);
        }
      }
    }
  }, [setBookmarks, isAuthenticated, user]);

  // Show offences list when we have filtered results
  const showOffencesList = searchQuery.trim() || selectedCategory;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {setPage && (
        <button
          onClick={() => goBack ? goBack() : setPage('home')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
            <Scale className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Offences & Fines</h1>
            <p className="text-emerald-100">
              {offences.length} offences • {categories.length} categories
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          onClear={() => { setSearchQuery(''); setExpandedOffence(null); }}
          recentSearches={recentSearches}
          onSelectRecent={(term) => { setSearchQuery(term); handleSearchSubmit(); }}
          onClearRecent={() => setRecentSearches([])}
          suggestions={searchSuggestions}
          onSelectSuggestion={handleSelectSuggestion}
        />
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="offences" />

      <div>
        {/* Bookmarked Section */}
        <BookmarkedSection
          offences={offences}
          bookmarks={bookmarks}
          onSelect={(offence) => {
            setSelectedCategory(offence.category);
            setExpandedOffence(offence);
            scrollToOffences();
          }}
          onClearAll={handleClearBookmarks}
        />

        {/* Categories */}
        <div className="mb-6">
          <div className="flex flex-col items-center mb-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <h2 className="font-bold text-gray-800">Browse by Category</h2>
            </div>
            {selectedCategory && (
              <button
                onClick={() => { setSelectedCategory(null); setExpandedOffence(null); }}
                className="text-sm text-red-600 font-semibold hover:text-red-700"
              >
                Clear Selection
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {categories.map(category => (
              <CategoryCard
                key={category.id}
                category={category}
                offenceCount={categoryOffenceCounts[category.id]}
                isSelected={selectedCategory === category.id}
                onClick={() => handleSelectCategory(category.id)}
              />
            ))}
          </div>
        </div>

        {/* Inline Ad Slot 1 */}
        <InlineBanner1 page="offences" />

        {/* Results Summary */}
        {showOffencesList && (
          <div ref={offencesListRef} className="mb-4 flex items-center justify-between pt-2">
            <span className="text-sm text-gray-600">
              {filteredOffences.length} offence{filteredOffences.length !== 1 ? 's' : ''} found
            </span>
            {selectedCategory && (
              <span
                className="text-xs font-medium px-3 py-1 rounded-full"
                style={{
                  backgroundColor: categories.find(c => c.id === selectedCategory)?.color + '20',
                  color: categories.find(c => c.id === selectedCategory)?.color
                }}
              >
                {categories.find(c => c.id === selectedCategory)?.name}
              </span>
            )}
          </div>
        )}

        {/* Offences List */}
        {showOffencesList ? (
          <div className="space-y-3">
            {filteredOffences.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">No offences found</h3>
                <p className="text-gray-500">
                  Try adjusting your search or filters
                </p>
              </div>
            ) : (
              filteredOffences.map(offence => (
                <OffenceCard
                  key={offence.code}
                  offence={offence}
                  category={getCategoryForOffence(offence)}
                  isExpanded={expandedOffence?.code === offence.code}
                  onToggle={() => handleToggleExpand(offence)}
                  allOffences={offences}
                  onViewRelated={handleViewRelated}
                  isBookmarked={bookmarks.includes(offence.code)}
                  onToggleBookmark={() => handleToggleBookmark(offence.code)}
                  searchQuery={searchQuery}
                />
              ))
            )}
          </div>
        ) : (
          /* Initial State - No category selected */
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Scale className="w-10 h-10 text-blue-500" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-2">Select a Category</h3>
            <p className="text-gray-500 max-w-xs mx-auto">
              Choose a category above to browse offences, or use the search bar to find specific offences
            </p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-3">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-bold text-amber-800 mb-1">Legal Disclaimer</h4>
                <p className="text-sm text-amber-700 leading-relaxed">
                  This information is based on the Road Traffic Act, 2018 and Road Traffic
                  Regulations, 2022. Fine amounts and penalties may be updated. For specific
                  legal matters, consult a licensed attorney.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Banner Ad */}
        <BottomBanner page="offences" />
      </div>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Bookmark className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Sign In to Save</h3>
                  <p className="text-emerald-100 text-sm">Keep your saved offences across devices</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-gray-700">
                    Sign in or create an account to save offences and access them from your dashboard at any time.
                  </p>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Save offences across all your devices
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      View saved offences in your dashboard
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Track your documents and tickets
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowLoginPrompt(false);
                    if (setPage) setPage('login');
                  }}
                  className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 p-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-full shadow-2xl transition-all transform hover:scale-110"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default OffencesPage;
